// react-router shim — v5 side. Phase 4 (react-router 5 -> 6).
//
// The whole app imports from `vendor/router-ROUTER_TARGET`, never from
// `react-router-dom` directly; webpack rewrites that to this file (default) or
// to `router-v6.js` when started with `-r v6`. See config/env.js + the
// NormalModuleReplacementPlugin in config/webpack.config.js.
//
// Direction of the emulation matters: we write the app in the **v6 dialect**
// and emulate it on top of v5 here — not the reverse. This file is scaffolding
// meant to be deleted once the migration lands, so nothing new should ever be
// written against the v5 API.
//
// What is NOT emulated, on purpose:
//   Routes / Outlet / relative paths -> these are structural, a shim cannot
//   fake them (a v5 <Switch> rejects `element=`, and relative vs absolute child
//   paths are mutually exclusive). Route-declaring files are therefore the one
//   surface that has to exist in two versions. See OPTIMIZATION_PLAN Phase 4.
//   useResolvedPath -> deliberately absent rather than half-working: it only
//   makes sense once child routes are relative, which is v6-only.

import { createElement, useCallback } from 'react';
import { useHistory, useRouteMatch, Redirect } from 'react-router-dom';

// Passes through everything v5 has, including the legacy `useHistory` /
// `useRouteMatch` still used by files not yet converted.
export * from 'react-router-dom';

// v6 `useNavigate` on top of v5 `useHistory`.
export const useNavigate = () => {
  const history = useHistory();
  return useCallback( (to, options = {}) => {
    if (typeof to === 'number') { history.go(to); return; } // navigate(-1)
    (options.replace) ? history.replace(to) : history.push(to);
  }, [history]);
};

// v6 `useMatch` on top of v5 `useRouteMatch`.
//
// ⚠️ The default is INVERTED between the two, this is not a rename: v6 matches
// the whole pathname unless told otherwise (`end: true`), v5 matches by prefix
// (`exact: false`). Call sites must therefore pass `{ end: false }` explicitly
// wherever they relied on v5 prefix matching.
//
// The return value is normalised to the v6 shape { params, pathname, pattern }.
// v5's `url` / `path` / `isExact` are NOT re-exposed: consumers that read
// `isExact` (SubMenu.jsx) have to ask for it up front via `{ end: true }`, which
// is the v6 idiom and works on both versions.
export const useMatch = (pattern) => {
  const p = (typeof pattern === 'string') ? { path: pattern, end: true } : { end: true, ...pattern };
  const match = useRouteMatch({ path: p.path, exact: p.end });
  return (match)
    ? { params: match.params, pathname: match.url, pattern: { path: p.path, end: p.end } }
    : null;
};

// v6 `<Navigate>` on top of v5 `<Redirect>`. Note the flipped default: v6
// pushes unless `replace`, v5 replaces unless `push`.
export function Navigate({ to, replace = false, state = null }) {
  return createElement(Redirect, { to: (state) ? { pathname: to, state } : to, push: !replace });
}
